import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import {
  COUNTRY_CODES, DEFAULT_DIAL, dialOf, toE164, phoneProblem, formatE164, splitPhone,
} from '../constants/phone';
import { sendVerificationCode, clearRecaptcha, phoneErrorKey } from '../firebase/phoneAuth';
import { isPhoneRegistered } from '../firebase/phoneIndex';
import './PhoneAuthPanel.css';

const CODE_LEN = 6;

// ── GOOGLE ATTRIBUTION ───────────────────────────────────────────────────────
// The badge is hidden in App.css, which Google's terms permit ONLY if this is
// shown in its place. It is therefore rendered on both steps, not just the one
// that triggers the challenge.
//
// Built from a template with {privacy} and {terms} placeholders rather than
// concatenated fragments, so a translator controls word ORDER — Russian and
// Armenian do not put "Privacy Policy and Terms of Service" where English does,
// and gluing pieces together in JSX order would force them to.
function RecaptchaNote({ t }) {
  const LINKS = {
    privacy: { href: 'https://policies.google.com/privacy', label: t('phone_recaptcha_privacy') },
    terms:   { href: 'https://policies.google.com/terms',   label: t('phone_recaptcha_terms') },
  };
  return (
    <p className="pa-legal">
      {t('phone_recaptcha_note').split(/\{(privacy|terms)\}/).map((part, i) => {
        const link = LINKS[part];
        if (!link) return <React.Fragment key={i}>{part}</React.Fragment>;
        return (
          <a key={i} href={link.href} target="_blank" rel="noreferrer noopener">
            {link.label}
          </a>
        );
      })}
    </p>
  );
}
// Long enough that the first SMS has realistically arrived, short enough that a
// genuinely lost message is not a dead end. Also the cheapest rate limit there
// is: every resend is a paid message and counts against the project quota.
const RESEND_SECONDS = 45;

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-3.8 3.8a5.5 5.5 0 1 1-7.8 7.8 5.5 5.5 0 0 1 7.8-7.8zm0 0L19 4m0 0l2.5 2.5" />
    </svg>
  );
}

// ── SIGN IN WITH A PHONE NUMBER ──────────────────────────────────────────────
// Two steps in one component: the number, then the code it caused to be sent.
// They share the ConfirmationResult, the reCAPTCHA and the error line, so
// splitting them would mean lifting all three into whatever rendered both.
//
// EMBEDDED, NOT A MODAL. It renders inside whatever auth surface hosts it —
// /login, /register, the guest modal — using the same `af-*` classes as the
// email form, so it inherits the design system instead of restating it.
//
// WHAT IT DOES NOT DO: decide where the user goes next. It reports
// `{ user, isNewAccount }` through `onDone` exactly as the Google button does,
// because a new account has not been asked customer-or-designer yet and only
// the host page knows where that question belongs in its own flow.
// `onStart` fires the instant before the credential is confirmed. The host page
// needs that moment: signing in makes `user` truthy, and /login has an effect
// that redirects on exactly that — it would win the race and send a brand-new
// account to the dashboard instead of to the customer-or-designer question.
// `initialNumber` prefills the form from somewhere else — the combined sign-in
// field, where the reader has already typed their number once and should not be
// asked for it again. `autoSend` goes one step further and sends the code on
// mount, so typing a number into that field and pressing the button lands
// straight on the OTP step rather than on a second form asking the same thing.
// ── WHAT THE PANEL IS BEING USED FOR ─────────────────────────────────────────
// The three callers ask the same question — "prove this number is yours" — and
// then want three different things from the answer. The difference cannot live
// at the call site, because it is decided AFTER the code is confirmed, inside
// `verify`, where only this component knows whether the account already
// existed.
//
//   'signin'  /login. Any outcome is fine; go straight through.
//   'signup'  /register. An EXISTING account here is worth saying out loud —
//             Firebase has no "already registered" error for phone auth, it
//             simply signs the person in, which reads as the sign-up form
//             silently doing something else. See the `exists` step.
//   'reset'   /forgot-password. The code IS the proof of ownership, so the
//             password step is shown whether or not the account is new. This
//             is the whole reset: there is no emailed link in it.
export const MODES = { SIGN_IN: 'signin', SIGN_UP: 'signup', RESET: 'reset' };

/**
 * Which screen follows a confirmed code.
 *
 * PURE, AND EXPORTED, because it is the only real decision in this file and it
 * is otherwise unreachable: `verify` can only be driven by sending a live SMS.
 * As a function it can be checked against every mode/outcome pair without one.
 *
 * 'done' means "hand back to the caller", not a step that renders.
 *
 *   mode     isNewAccount   →
 *   reset    either         → password   the code IS the proof; that is the reset
 *   signup   false          → exists     say so; Firebase just signs them in
 *   signup   true           → password   offer one while they are here
 *   signin   true           → password   same offer, whichever door they came in
 *   signin   false          → done       returning user, nothing to add
 */
export function nextStepAfterVerify(mode, isNewAccount) {
  if (mode === MODES.RESET) return 'password';
  if (mode === MODES.SIGN_UP && !isNewAccount) return 'exists';
  if (isNewAccount) return 'password';
  return 'done';
}

export default function PhoneAuthPanel({
  onDone, onCancel, onStart, disabled = false, initialNumber = '', autoSend = false,
  mode = MODES.SIGN_IN,
}) {
  const { loginWithPhone, setPhonePassword } = useAuth();
  const { t } = useT();

  // Parsed ONCE, at mount. `initialNumber` is what was typed elsewhere; after
  // that this form owns the value, and re-parsing would fight the user's edits.
  const seed = useRef(splitPhone(initialNumber)).current;

  // 'phone' | 'code' | 'exists' | 'password'
  const [step, setStep] = useState('phone');
  // Held between the OTP step and the optional password step: the reader is
  // already signed in by then, and this is what `onDone` is finally given
  // whether they set a password or skipped.
  const doneRef = useRef(null);
  const [pw, setPw] = useState('');
  const [dial, setDial] = useState(seed.dial);
  const [national, setNational] = useState(seed.national);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [wait, setWait] = useState(0);           // resend cooldown, seconds
  const [sentTo, setSentTo] = useState('');

  // The ConfirmationResult. A ref, not state: it is a handle to an in-flight
  // verification, nothing renders from it, and putting it in state would make
  // every keystroke in the code field a dependency of it.
  const confirmationRef = useRef(null);
  const codeRef = useRef(null);

  // `onDone` unmounts this panel, so anything after an await needs to know.
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  // THE CLEANUP THE WHOLE THING DEPENDS ON. The widget and its global callbacks
  // survive unmount on their own — see firebase/phoneAuth.
  useEffect(() => () => clearRecaptcha(), []);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const id = setTimeout(() => setWait(w => w - 1), 1000);
    return () => clearTimeout(id);
  }, [wait]);

  // The code field is the only thing on screen at that point, so focusing it
  // saves a tap on the device most likely to be reading the SMS.
  useEffect(() => { if (step === 'code') codeRef.current?.focus(); }, [step]);

  const autoSent = useRef(false);

  const send = useCallback(async () => {
    const problem = phoneProblem(dial, national);
    if (problem) { setError(t(problem)); return; }

    setBusy(true);
    setError('');
    try {
      const e164 = toE164(dial, national);

      // ── ASK THE DATABASE BEFORE PAYING FOR A MESSAGE ──────────────────────
      // Only in sign-up. Signing in and resetting a password are both things
      // you do to an account that already exists, so a hit there is the normal
      // case and stopping on it would break both.
      //
      // A single point read of `phoneIndex/{digits}` — see firebase/phoneIndex.
      // It fails OPEN, so a rules or network problem sends the code as before
      // rather than locking anyone out; the post-verification `exists` step is
      // still there to catch what this misses.
      if (mode === MODES.SIGN_UP && await isPhoneRegistered(e164)) {
        if (!alive.current) return;
        setSentTo(e164);
        setStep('exists');
        return;                      // no SMS, no reCAPTCHA, nothing spent
      }

      confirmationRef.current = await sendVerificationCode(e164);
      if (!alive.current) return;
      setSentTo(e164);
      setCode('');
      setStep('code');
      setWait(RESEND_SECONDS);
    } catch (err) {
      if (alive.current) setError(t(phoneErrorKey(err)));
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [dial, national, t, mode]);

  // ── SEND ON MOUNT, WHEN ASKED ─────────────────────────────────────────────
  // Declared AFTER `send` so it never reads a binding that has not been
  // initialised — an effect placed above it would work, and would break the
  // moment anyone moved a line.
  //
  // Guarded by a ref rather than by a dependency list, because an SMS costs
  // money and reaches a real phone: React 18's StrictMode runs mount effects
  // twice in development, and any re-render that changed `send`'s identity
  // would be another message. Once per mounted panel, whatever React does
  // around it.
  useEffect(() => {
    if (!autoSend || autoSent.current) return;
    if (!seed.national) return;              // nothing usable was handed over
    autoSent.current = true;
    send();
  }, [autoSend, seed.national, send]);

  const verify = useCallback(async () => {
    if (code.length !== CODE_LEN) { setError(t('phone_err_code_required')); return; }
    // The tab was left open long enough for the handle to be lost, or the page
    // was restored from cache. Sending again is the only way forward.
    if (!confirmationRef.current) {
      setError(t('phone_err_expired_session'));
      setStep('phone');
      return;
    }

    setBusy(true);
    setError('');
    try {
      onStart?.();
      const result = await loginWithPhone(confirmationRef.current, code);
      // Signed in — the challenge is spent and the widget has no further use.
      clearRecaptcha();
      confirmationRef.current = null;

      // Held for whichever branch below hands control back to the caller, and
      // read by the password step to word its own intro.
      doneRef.current = result;

      // The whole decision, in one place — see nextStepAfterVerify above for
      // what each outcome means and why.
      //
      // Worth restating the non-obvious one: a SIGN-UP with an existing number
      // is not a failure and is not undone. Firebase has no "number already in
      // use" error, because the same number is the same identity — it signs the
      // person in. They proved the number is theirs, so they ARE the account
      // holder; the `exists` step exists so a sign-up form cannot silently
      // behave like a sign-in form.
      const next = nextStepAfterVerify(mode, !!result?.isNewAccount);
      if (next !== 'done') {
        setStep(next);
        return;
      }
      onDone?.(result);
      return;                        // onDone navigates; touch no state after it
    } catch (err) {
      if (!alive.current) return;
      setError(t(phoneErrorKey(err)));
      setCode('');
      codeRef.current?.focus();
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [code, loginWithPhone, onDone, onStart, t, mode]);

  // ── THE OPTIONAL PASSWORD ─────────────────────────────────────────────────
  // Skipping is a real choice, not a way to fail: the account works perfectly
  // well with codes alone, and one can be set later in Settings. So both paths
  // end in the same `onDone` and neither blocks the sign-up.
  const finish = useCallback(() => { onDone?.(doneRef.current); }, [onDone]);

  const savePassword = useCallback(async () => {
    if (pw.length < 6) { setError(t('auth_err_pass_min')); return; }
    setBusy(true);
    setError('');
    try {
      await setPhonePassword(pw);
      finish();
    } catch (err) {
      if (!alive.current) return;
      // A failure here must not strand a signed-in user on a form they cannot
      // leave — the account exists either way, so the error is shown and the
      // Skip button remains the way out.
      setError(err?.code === 'auth/weak-password'
        ? t('auth_err_pass_min') : t('auth_err_password_not_set'));
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [pw, setPhonePassword, finish, t]);

  function backToNumber() {
    confirmationRef.current = null;
    clearRecaptcha();
    setStep('phone');
    setCode('');
    setError('');
    setWait(0);
  }

  const locked = disabled || busy;
  const canSend = !locked && !phoneProblem(dial, national);
  const canVerify = !locked && code.length === CODE_LEN;

  return (
    <div className="pa">
      {/* No reCAPTCHA container here. It lives in public/index.html, outside
          #root, because grecaptcha holds a reference to the node it renders
          into and React must not be able to replace or move it. */}
      {error && (
        <div className="af-toast af-toast--error pa-error" role="alert">
          <span className="af-toast__icon">⚠</span>
          {error}
        </div>
      )}

      {step === 'exists' ? (
        /* ── THIS NUMBER ALREADY HAS AN ACCOUNT ──────────────────────────────
           Not an error state, and deliberately not styled as one — in neither
           of the two ways it is reached.

           BEFORE THE SMS (doneRef empty). `phoneIndex` already knew the number,
           so no code was sent and nobody is signed in. The way forward is the
           sign-in page; `finish` must NOT be called here, because it hands
           `onDone` the null that was never filled in and RegisterForm
           destructures its argument.

           AFTER IT (doneRef set). The code was confirmed, which SIGNED THEM IN
           — Firebase has no "number already in use" error for phone auth,
           because the same number is the same identity. They proved the number
           is theirs, so they are the account holder and Continue is honest.
           This is the backstop for numbers the index has not caught up with. */
        <div className="pa-exists">
          <p className="pa-sent">
            {t('phone_exists_title').replace('{number}', formatE164(sentTo))}
          </p>
          {doneRef.current ? (
            <>
              <p className="pa-hint pa-hint--block">{t('phone_exists_body')}</p>
              <button className="af-btn" type="button" onClick={finish} disabled={busy}>
                {t('phone_exists_continue')}
              </button>
            </>
          ) : (
            <>
              <p className="pa-hint pa-hint--block">{t('phone_taken_body')}</p>
              <Link className="af-btn pa-btn-link" to="/login">
                {t('phone_taken_signin')}
              </Link>
              <button type="button" className="pa-link pa-link--center" onClick={backToNumber}>
                {t('phone_change')}
              </button>
            </>
          )}
        </div>
      ) : step === 'password' ? (
        /* Signed in already. In sign-up this adds the SECOND way in — a
           password for the number — and skipping leaves the account reachable
           by code alone. In reset it IS the point of the visit, so the copy
           says "new password" and the skip is the way to back out. */
        <form onSubmit={(e) => { e.preventDefault(); savePassword(); }} noValidate>
          {/* THREE INTROS, ONE FORM. Resetting a password and setting one for
              the first time are the same screen doing different jobs, and the
              third case is the one worth naming: a reset on a number that had
              no account signs in AND creates it, because that is what
              confirming an SMS code does. Saying so beats letting someone
              conclude their projects have vanished. */}
          <p className="pa-sent">
            {mode !== MODES.RESET ? t('phone_pw_intro')
              : doneRef.current?.isNewAccount ? t('phone_pw_reset_new')
                : t('phone_pw_reset_intro')}
          </p>

          <div className="af-field">
            <label className="af-label" htmlFor="pa-pw">{t('auth_password_label')}</label>
            <div className="af-input-wrap">
              <span className="af-input-icon">
                <svg viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor"
                  strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
              </span>
              <input
                id="pa-pw"
                className="af-input"
                type="password"
                // `new-password` so a manager offers to generate and store one
                // rather than autofilling the password for some other site.
                autoComplete="new-password"
                placeholder={t('auth_password_ph')}
                value={pw}
                onChange={(e) => { setPw(e.target.value); setError(''); }}
                disabled={locked}
                autoFocus
              />
            </div>
            <p className="pa-hint">{t('phone_pw_hint')}</p>
          </div>

          <button className="af-btn" type="submit" disabled={locked || pw.length < 6}>
            {busy && <span className="af-spinner" />}
            {busy ? t('auth_signin_ing')
              : mode === MODES.RESET ? t('phone_pw_reset_save') : t('phone_pw_save')}
          </button>

          {/* Still an escape hatch in reset mode: the sign-in already happened,
              so leaving without setting a password is a valid outcome rather
              than a half-finished one. */}
          <button type="button" className="pa-link pa-link--center"
            onClick={finish} disabled={busy}>
            {mode === MODES.RESET ? t('phone_pw_reset_skip') : t('phone_pw_skip')}
          </button>
        </form>
      ) : step === 'phone' ? (
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          noValidate
        >
          <div className="af-field">
            <label className="af-label" htmlFor="pa-number">{t('phone_label')}</label>
            <div className="pa-tel">
              <select
                className="pa-dial"
                aria-label={t('phone_country')}
                value={dial}
                onChange={(e) => { setDial(e.target.value); setError(''); }}
                disabled={locked}
              >
                {COUNTRY_CODES.map(c => (
                  <option key={c.iso} value={c.dial}>
                    {c.flag} {c.name} ({dialOf(c.dial)})
                  </option>
                ))}
              </select>

              <div className="af-input-wrap pa-num-wrap">
                <span className="af-input-icon"><PhoneIcon /></span>
                <input
                  id="pa-number"
                  className="af-input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  placeholder={t('phone_ph')}
                  value={national}
                  onChange={(e) => { setNational(e.target.value); setError(''); }}
                  disabled={locked}
                />
              </div>
            </div>
            <p className="pa-hint">{t('phone_hint')}</p>
          </div>

          <button className="af-btn" type="submit" disabled={!canSend}>
            {busy && <span className="af-spinner" />}
            {busy ? t('phone_sending') : t('phone_send')}
          </button>

          <RecaptchaNote t={t} />

          {onCancel && (
            <button type="button" className="pa-link pa-link--center"
              onClick={onCancel} disabled={busy}>
              {t('auth_phone_back_email')}
            </button>
          )}
        </form>
      ) : (
        <form
          onSubmit={(e) => { e.preventDefault(); verify(); }}
          noValidate
        >
          <p className="pa-sent">
            {t('phone_code_sent')}<br />
            <strong>{formatE164(sentTo)}</strong>
          </p>

          <div className="af-field">
            <label className="af-label" htmlFor="pa-code">{t('phone_code_label')}</label>
            <div className="af-input-wrap">
              <span className="af-input-icon"><KeyIcon /></span>
              <input
                id="pa-code"
                ref={codeRef}
                className="af-input pa-code"
                type="text"
                inputMode="numeric"
                // Lets iOS and Android offer the code straight from the SMS
                // rather than making the reader memorise six digits.
                autoComplete="one-time-code"
                maxLength={CODE_LEN}
                placeholder="––––––"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D+/g, '').slice(0, CODE_LEN));
                  setError('');
                }}
                disabled={locked}
              />
            </div>
          </div>

          <button className="af-btn" type="submit" disabled={!canVerify}>
            {busy && <span className="af-spinner" />}
            {busy ? t('phone_verifying') : t('phone_verify')}
          </button>

          <div className="pa-actions">
            <button type="button" className="pa-link" onClick={backToNumber} disabled={busy}>
              {t('phone_change')}
            </button>
            <button type="button" className="pa-link" onClick={send} disabled={busy || wait > 0}>
              {wait > 0 ? t('phone_resend_in').replace('{n}', wait) : t('phone_resend')}
            </button>
          </div>

          {/* Also here: Resend runs the challenge again, so the attribution has
              to be on this step too, not only the one that first triggered it. */}
          <RecaptchaNote t={t} />
        </form>
      )}
    </div>
  );
}
